import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { OperatingModeType } from '@/api/admin-sales-channels-types'
import { OperatingMode } from '@/api/admin-sales-channels-types'
import { useCreateDevice } from '@/api/api-mutations'
import {
	preloadListPointsOfSale,
	useListDevices,
	useListMarketingChannels,
	useListPointsOfSale
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

const OPERATING_MODE_VALUES = Object.values(OperatingMode) as OperatingModeType[]

export const Route = createFileRoute('/_authenticated/settings/devices/create')({
	component: RouteComponent,
	loader: async ({ context }) => {
		await preloadListPointsOfSale(context, {})
	},
	staticData: {
		crumb: ({ context: { L } }) => L.General.Create()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<NewDeviceForm />
		</RouteModal>
	)
}

function NewDeviceForm() {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()

	const createDevice = useCreateDevice()
	const pointsOfSale = useListPointsOfSale()
	const devices = useListDevices()
	const marketingChannels = useListMarketingChannels()

	const operatingModeOptions = useMemo(
		() => OPERATING_MODE_VALUES.map(value => ({ label: L.Settings.PointOfSale.OperatingMode(value), value })),
		[L]
	)

	// A POS already running a device can't take another without revoking that device (see
	// revokeAndUnlinkOtherPosDevices); disable those options and steer the user to unlink first.
	const occupiedPosUuids = useMemo(
		() => new Set((devices.data ?? []).filter(d => d.pointOfSale).map(d => d.pointOfSale!.uuid)),
		[devices.data]
	)

	const pointOfSaleOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.NotLinked() },
			...(pointsOfSale.data ?? []).map(pos => {
				const occupied = occupiedPosUuids.has(pos.uuid)
				return {
					value: pos.uuid,
					label: occupied ? L.Settings.Devices.PointOfSaleOccupiedOption(pos.name) : pos.name,
					disabled: occupied
				}
			})
		],
		[L, pointsOfSale.data, occupiedPosUuids]
	)

	const marketingChannelOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.Devices.NoMarketingChannel() },
			...(marketingChannels.data ?? []).map(mc => ({ value: mc.uuid, label: mc.name }))
		],
		[L, marketingChannels.data]
	)

	const form = useAppForm({
		defaultValues: {
			name: '',
			identifier: '',
			allowedOperatingModes: [] as OperatingModeType[],
			pointOfSaleUuid: '',
			marketingChannelUuid: ''
		},
		onSubmit: async ({ value }) => {
			const result = await createDevice({
				body: {
					name: value.name,
					identifier: value.identifier.trim() || undefined,
					allowedOperatingModes: value.allowedOperatingModes,
					pointOfSaleUuid: value.pointOfSaleUuid || undefined,
					marketingChannelUuid: value.marketingChannelUuid || undefined
				}
			})
			if (result.isSuccess) {
				createdToast(L.Settings.Devices.CreateSuccess(), {
					to: '/settings/devices/$deviceUuid',
					params: { deviceUuid: result.response.uuid }
				})
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.Devices.CreateTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							children={field => (
								<field.TextField
									label={L.Fields.Name()}
									placeholder={L.Settings.Devices.NamePlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='identifier'
							children={field => (
								<field.TextField
									label={L.Settings.Devices.Identifier()}
									placeholder={L.Settings.Devices.IdentifierPlaceholder()}
									optional
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='allowedOperatingModes'
							children={field => (
								<field.ComboboxField
									label={L.Settings.Devices.OperatingModes()}
									placeholder={L.Settings.PointOfSale.SelectOperatingModes()}
									multiple
									options={operatingModeOptions}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='pointOfSaleUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.Devices.LinkedPointOfSale()}
									options={pointOfSaleOptions}
									optionsLoading={pointsOfSale.isLoading || devices.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.Devices.LinkDescription()}
						</Text>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='marketingChannelUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.Devices.MarketingChannel()}
									options={marketingChannelOptions}
									optionsLoading={marketingChannels.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.Devices.MarketingChannelDescription()}
						</Text>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
